import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { ArrowLeftRight, Diamond, Plus } from 'lucide-react';
import { keyframeAbsoluteTime, visibleKeyframes } from '../../lib/creatorAnimation';
import { previousTransitionClip } from '../../lib/creatorP1';
import { clamp, findClip, formatTime, moveClips, projectDuration, snapTime, trimClip } from '../../lib/creatorTimeline';
import { anchoredScrollLeft, clampZoom, clientXToTime, displayDuration, fitZoom, pixelsToTime, rulerStep, timelineWidth, timeToPixels, TRACK_HEADER_WIDTH, wheelPixels } from '../../lib/creatorTimeScale';
import CreatorTrackHeader from './CreatorTrackHeader';
import CreatorVideoThumbnails from './CreatorVideoThumbnails';
import { clipCount, compatibleTrack, insertAssets, moveToTrack, TRACK_HEIGHTS } from '../../lib/creatorEditing';
import './creatorTimelineInteraction.css';

const ASSET_MIME = 'application/x-steelmora-asset';
const hasAssetType = transfer => Array.from(transfer?.types || []).includes(ASSET_MIME);
const clipChanged = (before, after, ids) => ids.some(id => {
  const previous = findClip(before, id), next = findClip(after, id);
  return previous && next && (previous.track.id !== next.track.id || previous.clip.start !== next.clip.start
    || previous.clip.duration !== next.clip.duration || previous.clip.source_start !== next.clip.source_start);
});

export const CreatorWaveform = memo(function CreatorWaveform({ asset, clip }) {
  const canvas = useRef(null);
  useEffect(() => {
    const node = canvas.current;
    if (!node || !Array.isArray(asset.peaks) || !asset.peaks.length) return undefined;
    const paint = () => {
      const width = Math.max(1, Math.min(2048, node.clientWidth));
      const height = 24;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      node.width = width * dpr;
      node.height = height * dpr;
      const ctx = node.getContext('2d');
      if (!ctx) return;
      ctx.scale(dpr, dpr);
      ctx.fillStyle = '#68e6da';
      for (let x = 0; x < width; x += 2) {
        const from = Math.floor((clip.source_start + x / width * clip.duration * clip.speed) / asset.duration * asset.peaks.length);
        const to = Math.ceil((clip.source_start + (x + 2) / width * clip.duration * clip.speed) / asset.duration * asset.peaks.length);
        let value = 0;
        for (let index = Math.max(0, from); index < Math.min(asset.peaks.length, Math.max(from + 1, to)); index += 1) {
          const peak = asset.peaks[index];
          value = Math.max(value, ...(Array.isArray(peak) ? peak.map(Math.abs) : [Math.abs(peak)]));
        }
        const amplitude = clamp(value, 0, 1) * (height - 2);
        ctx.fillRect(x, (height - amplitude) / 2, 1, Math.max(1, amplitude));
      }
    };
    paint();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(paint);
    observer.observe(node);
    return () => observer.disconnect();
  }, [asset.peaks, asset.duration, clip.source_start, clip.duration, clip.speed]);
  return <canvas ref={canvas} className="creator-waveform" aria-hidden="true" />;
}, (previous, next) => previous.asset.id === next.asset.id && previous.asset.peaks === next.asset.peaks
  && previous.asset.duration === next.asset.duration && previous.clip.source_start === next.clip.source_start
  && previous.clip.duration === next.clip.duration && previous.clip.speed === next.clip.speed);

const CreatorTimeline = forwardRef(function CreatorTimeline({ project, selected = [], onSelect, time = 0, onSeek, onChange, zoom = 1, onZoom, sessionUrl = path => path, playing = false, followPlayhead = false, onClipContext, onTransitionSelect }, ref) {
  const scroll = useRef(null);
  const heading = useRef(null);
  const drag = useRef(null);
  const gesture = useRef(null);
  const gestureFrame = useRef(null);
  const scrollFrame = useRef(null);
  const dragFrame = useRef(null);
  const draggedAsset = useRef(null);
  const pendingAnchor = useRef(null);
  const [draft, setDraft] = useState(null);
  const [interaction, setInteraction] = useState('');
  const [localZoom, setLocalZoom] = useState(() => clampZoom(zoom));
  const [geometry, setGeometry] = useState({ width: 0, headerWidth: TRACK_HEADER_WIDTH });
  const [visibleLeft, setVisibleLeft] = useState(0);
  const [dropFeedback, setDropFeedback] = useState(null);
  const [snapGuide, setSnapGuide] = useState(null);
  const scaleZoom = typeof onZoom === 'function' ? clampZoom(zoom) : localZoom;
  const zoomRef = useRef(scaleZoom);
  const renderedZoom = useRef(scaleZoom);
  const total = projectDuration(project);
  const latest = useRef(null);
  latest.current = { project, selected, onSelect, onSeek, onChange, onZoom, time, total, onClipContext };
  const current = draft || project;
  const usableWidth = Math.max(0, geometry.width - geometry.headerWidth);
  const duration = displayDuration(total);
  const width = timelineWidth(total, scaleZoom, usableWidth);
  const step = rulerStep(scaleZoom);
  const firstTick = Math.max(0, Math.floor(pixelsToTime(visibleLeft - 100, scaleZoom) / step));
  const lastTick = Math.min(Math.floor(duration / step), Math.ceil(pixelsToTime(visibleLeft + usableWidth + 100, scaleZoom) / step));
  const ticks = Array.from({ length: Math.max(0, Math.min(256, lastTick - firstTick + 1)) }, (_, index) => firstTick + index);

  const readGeometry = useCallback(() => {
    const node = scroll.current;
    const rect = node?.getBoundingClientRect();
    const headerWidth = heading.current?.getBoundingClientRect().width || TRACK_HEADER_WIDTH;
    return { left: (rect?.left || 0) + (node?.clientLeft || 0), width: node?.clientWidth || rect?.width || 0, headerWidth };
  }, []);
  useLayoutEffect(() => {
    const measure = () => {
      const next = readGeometry();
      setGeometry(previous => previous.width === next.width && previous.headerWidth === next.headerWidth ? previous : { width: next.width, headerWidth: next.headerWidth });
    };
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure);
      observer.observe(scroll.current);
      observer.observe(heading.current);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [readGeometry]);
  useEffect(() => setLocalZoom(clampZoom(zoom)), [zoom]);

  const anchorForZoom = useCallback((nextZoom, clientX, previousZoom = zoomRef.current) => {
    const node = scroll.current;
    const measured = readGeometry();
    const available = Math.max(0, measured.width - measured.headerWidth);
    const oldLeft = node?.scrollLeft || 0;
    const playheadPixel = timeToPixels(latest.current.time, previousZoom) - oldLeft;
    const anchorPixel = Number.isFinite(clientX) ? clamp(clientX - measured.left - measured.headerWidth, 0, available)
      : playheadPixel >= 0 && playheadPixel <= available ? playheadPixel : available / 2;
    const anchorTime = Number.isFinite(clientX) ? pixelsToTime(oldLeft + anchorPixel, previousZoom) : latest.current.time;
    return anchoredScrollLeft({ anchorTime, anchorPixel, zoom: nextZoom, maxScroll: timelineWidth(latest.current.total, nextZoom, available) - available });
  }, [readGeometry]);
  const commitZoom = useCallback((value, clientX, reset = false) => {
    const next = clampZoom(value);
    const left = reset ? 0 : anchorForZoom(next, clientX);
    pendingAnchor.current = { zoom: next, left };
    const previous = zoomRef.current;
    zoomRef.current = next;
    if (next === previous) {
      if (scroll.current) scroll.current.scrollLeft = left;
      setVisibleLeft(left); pendingAnchor.current = null;
    }
    if (typeof latest.current.onZoom === 'function') latest.current.onZoom(next);
    else setLocalZoom(next);
    return next;
  }, [anchorForZoom]);
  const zoomTo = useCallback((value, clientX) => commitZoom(value, clientX), [commitZoom]);
  const fit = useCallback(() => {
    const measured = readGeometry();
    return commitZoom(fitZoom(latest.current.total, Math.max(0, measured.width - measured.headerWidth)), undefined, true);
  }, [commitZoom, readGeometry]);
  useImperativeHandle(ref, () => ({ zoomTo, fit, getZoom: () => zoomRef.current }), [zoomTo, fit]);

  useLayoutEffect(() => {
    const node = scroll.current;
    if (!node) return;
    const pending = pendingAnchor.current;
    if (pending && pending.zoom === scaleZoom) node.scrollLeft = pending.left;
    else if (renderedZoom.current !== scaleZoom) node.scrollLeft = anchorForZoom(scaleZoom, undefined, renderedZoom.current);
    const maxScroll = Math.max(0, width - usableWidth);
    node.scrollLeft = clamp(node.scrollLeft, 0, maxScroll);
    setVisibleLeft(node.scrollLeft);
    pendingAnchor.current = null;
    renderedZoom.current = scaleZoom;
    zoomRef.current = scaleZoom;
  }, [scaleZoom, width, usableWidth, anchorForZoom]);

  useEffect(() => {
    const node = scroll.current;
    const wheel = event => {
      if (event.target.closest('input,select,textarea,[contenteditable="true"]')) return;
      const deltaX = wheelPixels(event.deltaX, event.deltaMode, node.clientWidth);
      const deltaY = wheelPixels(event.deltaY, event.deltaMode, node.clientHeight);
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        zoomTo(zoomRef.current * Math.exp(-clamp(deltaY, -600, 600) * 0.002), event.clientX);
      } else if (event.shiftKey || deltaX) {
        const measured = readGeometry();
        const available = Math.max(0, measured.width - measured.headerWidth);
        const maxScroll = timelineWidth(latest.current.total, zoomRef.current, available) - available;
        node.scrollLeft = clamp(node.scrollLeft + (event.shiftKey ? deltaX || deltaY : deltaX), 0, maxScroll);
        if (!event.shiftKey && deltaY) node.scrollTop += deltaY;
        event.preventDefault();
        setVisibleLeft(node.scrollLeft);
      }
    };
    node.addEventListener('wheel', wheel, { passive: false });
    return () => node.removeEventListener('wheel', wheel);
  }, [zoomTo, readGeometry]);

  const cancelGestureFrame = () => {
    if (gestureFrame.current !== null) window.cancelAnimationFrame(gestureFrame.current);
    gestureFrame.current = null;
  };
  useEffect(() => {
    setDraft(null); setInteraction(''); setDropFeedback(null); setSnapGuide(null);
    return () => {
      if (gestureFrame.current !== null) window.cancelAnimationFrame(gestureFrame.current);
      if (scrollFrame.current !== null) window.cancelAnimationFrame(scrollFrame.current);
      if (dragFrame.current !== null) window.cancelAnimationFrame(dragFrame.current);
      gestureFrame.current = null; scrollFrame.current = null; dragFrame.current = null;
      gesture.current = null; drag.current = null; draggedAsset.current = null;
    };
  }, [project.project_id]);

  useEffect(() => {
    // Drag data is readable at dragstart; browsers protect it during dragover.
    const rememberAsset = event => {
      const id = hasAssetType(event.dataTransfer) ? event.dataTransfer.getData(ASSET_MIME) : '';
      draggedAsset.current = latest.current.project.assets.find(asset => asset.id === id) || null;
    };
    const clearAsset = () => { draggedAsset.current = null; setDropFeedback(null); setSnapGuide(null); };
    document.addEventListener('dragstart', rememberAsset);
    document.addEventListener('dragend', clearAsset);
    return () => { document.removeEventListener('dragstart', rememberAsset); document.removeEventListener('dragend', clearAsset); };
  }, []);

  useEffect(() => {
    const node = scroll.current;
    if (!playing || !followPlayhead || !node || gesture.current || drag.current || !usableWidth) return;
    const position = timeToPixels(time, scaleZoom);
    const margin = Math.min(64, usableWidth / 4);
    if (position < node.scrollLeft + margin || position > node.scrollLeft + usableWidth - margin) {
      node.scrollLeft = clamp(position - usableWidth / 2, 0, Math.max(0, width - usableWidth));
      setVisibleLeft(node.scrollLeft);
    }
  }, [time, playing, followPlayhead, scaleZoom, usableWidth, width]);

  const capture = event => scroll.current?.setPointerCapture?.(event.pointerId);
  const release = pointerId => {
    const node = scroll.current;
    if (node && (!node.hasPointerCapture || node.hasPointerCapture(pointerId))) node.releasePointerCapture?.(pointerId);
  };
  const seekAt = clientX => latest.current.onSeek?.(clientXToTime(clientX, { ...readGeometry(), scrollLeft: scroll.current?.scrollLeft || 0, zoom: zoomRef.current, duration: latest.current.total }));
  const startScrub = (event, clearSelection = false) => {
    if (event.button !== 0 || gesture.current || drag.current || event.target.closest('button,input,select,[role="button"]')) return;
    event.preventDefault();
    if (clearSelection) onSelect?.([]);
    event.currentTarget.focus?.({ preventScroll: true });
    gesture.current = { type: 'scrub', pointerId: event.pointerId, x: event.clientX, y: event.clientY };
    capture(event); setInteraction('scrubbing'); seekAt(event.clientX);
  };
  const startPan = event => {
    if (event.button !== 1 || drag.current || gesture.current) return;
    event.preventDefault(); event.stopPropagation();
    gesture.current = { type: 'pan', pointerId: event.pointerId, startX: event.clientX, startY: event.clientY,
      left: scroll.current.scrollLeft, top: scroll.current.scrollTop, x: event.clientX, y: event.clientY };
    capture(event); setInteraction('panning');
  };
  const flushGesture = () => {
    const active = gesture.current;
    if (!active || !scroll.current) return;
    if (active.type === 'scrub') seekAt(active.x);
    else {
      const measured = readGeometry();
      const available = Math.max(0, measured.width - measured.headerWidth);
      scroll.current.scrollLeft = clamp(active.left + active.startX - active.x, 0, Math.max(0, timelineWidth(latest.current.total, zoomRef.current, available) - available));
      scroll.current.scrollTop = Math.max(0, active.top + active.startY - active.y);
      setVisibleLeft(scroll.current.scrollLeft);
    }
  };
  const moveGesture = event => {
    const active = gesture.current;
    if (!active || active.pointerId !== event.pointerId) return;
    active.x = event.clientX; active.y = event.clientY;
    if (gestureFrame.current !== null) return;
    gestureFrame.current = window.requestAnimationFrame(() => { gestureFrame.current = null; flushGesture(); });
  };
  const finishGesture = (event, cancelled = false) => {
    const active = gesture.current;
    if (!active || active.pointerId !== event.pointerId) return;
    cancelGestureFrame();
    if (!cancelled) { active.x = event.clientX; active.y = event.clientY; flushGesture(); }
    gesture.current = null; setInteraction(''); release(event.pointerId);
  };
  const startDrag = (event, clip, track, edge) => {
    if (event.button !== 0 || gesture.current || drag.current) return;
    event.preventDefault(); event.stopPropagation();
    const modified = event.ctrlKey || event.metaKey || event.shiftKey;
    const ids = selected.includes(clip.id) && !modified ? selected : modified
      ? selected.includes(clip.id) ? selected.filter(id => id !== clip.id) : [...selected, clip.id] : [clip.id];
    onSelect(ids);
    if (track.locked || modified || !ids.length || ids.some(id => findClip(project, id)?.track.locked)) return;
    capture(event);
    drag.current = { x: event.clientX, y: event.clientY, clientX: event.clientX, clientY: event.clientY,
      scroll: scroll.current.scrollLeft, pointerId: event.pointerId, clip, trackId: track.id, edge, ids,
      project, next: project, zoom: zoomRef.current, valid: true, moved: false };
    setInteraction(edge ? 'trimming' : 'dragging');
  };
  const laneAt = (clientX, clientY) => {
    const measured = readGeometry();
    const bounds = scroll.current.getBoundingClientRect();
    if (clientX < measured.left + measured.headerWidth || clientX > measured.left + measured.width || clientY < bounds.top || clientY > bounds.bottom) return null;
    const hit = document.elementFromPoint?.(clientX, clientY)?.closest('[data-track-lane]');
    if (hit && scroll.current.contains(hit)) return hit.dataset.trackLane;
    const lanes = [...scroll.current.querySelectorAll('[data-track-lane]')];
    const lane = lanes.find(node => { const box = node.getBoundingClientRect(); return box.height > 0 && clientY >= box.top && clientY < box.bottom; });
    return lane?.dataset.trackLane || null;
  };
  const feedback = (trackId, valid, message) => setDropFeedback(previous => previous?.trackId === trackId && previous?.valid === valid && previous?.message === message ? previous : { trackId, valid, message });
  const applyDrag = (value, active) => active.edge
    ? trimClip(value, active.clip.id, active.edge, active.trimTime, active.options)
    : active.targetId === active.trackId && active.ids.every(id => findClip(value, id)?.track.id === active.trackId)
      ? moveClips(value, active.ids, active.delta, active.options)
      : moveToTrack(value, active.ids, active.delta, active.targetId, active.options);
  const flushDrag = () => {
    const active = drag.current;
    if (!active) return;
    active.delta = pixelsToTime(active.clientX - active.x + scroll.current.scrollLeft - active.scroll, active.zoom);
    active.options = { threshold: pixelsToTime(8, active.zoom), playhead: latest.current.time };
    active.moved ||= Math.abs(active.clientX - active.x) > 2 || Math.abs(active.clientY - active.y) > 2;
    if (!active.moved) return;
    let guide = null;
    if (active.edge) {
      active.valid = true;
      const edge = active.edge === 'left' ? active.clip.start : active.clip.start + active.clip.duration;
      const raw = edge + active.delta;
      active.trimTime = snapTime(raw, active.project, { ...active.options, exclude: [active.clip.id] });
      if (Math.abs(active.trimTime - Math.max(0, raw)) > 0.00001) guide = active.trimTime;
    } else {
      active.targetId = laneAt(active.clientX, active.clientY);
      const target = active.project.tracks.find(track => track.id === active.targetId);
      const items = active.ids.map(id => findClip(active.project, id)).filter(Boolean);
      active.valid = Boolean(target && items.length && items.every(item => !item.track.locked && (item.clip.text
        ? !target.locked && ['text', 'overlay'].includes(target.type) : compatibleTrack(target, item.asset))));
      feedback(active.targetId, active.valid, active.valid ? `Mover a ${target.name}` : target?.locked ? 'Pista bloqueada' : target ? 'Pista incompatible' : 'Suelta sobre una pista compatible');
      if (!active.valid) { active.next = active.project; setDraft(null); setSnapGuide(null); return; }
    }
    active.next = applyDrag(active.project, active);
    if (projectDuration(active.next) > 1800) {
      active.valid = false; active.next = active.project;
      feedback(active.targetId || active.trackId, false, 'Se supera la duracion maxima del proyecto');
    }
    if (!active.edge && active.valid) {
      const items = active.ids.map(id => findClip(active.project, id)?.clip).filter(Boolean);
      const moved = findClip(active.next, active.clip.id)?.clip;
      const amount = moved ? moved.start - active.clip.start : 0;
      const requested = Math.max(-Math.min(...items.map(clip => clip.start)), active.delta);
      if (Math.abs(amount - requested) > 0.00001) {
        const edge = items.flatMap(clip => [clip.start, clip.start + clip.duration]).find(value => Math.abs(snapTime(value + requested, active.project, { ...active.options, exclude: active.ids }) - value - amount) < 0.00001);
        if (edge !== undefined) guide = edge + amount;
      }
    }
    setSnapGuide(guide);
    setDraft(active.valid ? active.next : null);
  };
  const moveDrag = event => {
    const active = drag.current;
    if (!active || active.pointerId !== event.pointerId) return;
    active.clientX = event.clientX; active.clientY = event.clientY;
    if (dragFrame.current !== null) return;
    dragFrame.current = window.requestAnimationFrame(() => { dragFrame.current = null; flushDrag(); });
  };
  const finishDrag = (event, cancelled = false) => {
    const active = drag.current;
    if (!active || active.pointerId !== event.pointerId) return;
    if (dragFrame.current !== null) window.cancelAnimationFrame(dragFrame.current);
    dragFrame.current = null;
    if (!cancelled) { active.clientX = event.clientX; active.clientY = event.clientY; flushDrag(); }
    drag.current = null; setDraft(null); setSnapGuide(null); setDropFeedback(null); setInteraction(''); release(active.pointerId);
    if (!cancelled && active.valid && active.moved && clipChanged(active.project, active.next, active.edge ? [active.clip.id] : active.ids)) {
      latest.current.onChange(value => applyDrag(value, active), active.edge ? 'Recortar' : 'Mover clips');
    }
  };
  const clipContext = (event, clipId) => {
    if (!onClipContext) return;
    event.preventDefault(); event.stopPropagation();
    if (!latest.current.selected.includes(clipId)) latest.current.onSelect([clipId]);
    latest.current.onClipContext(event, clipId);
  };
  const assetDrop = (event, trackId, commit = false) => {
    if (!hasAssetType(event.dataTransfer)) return;
    event.preventDefault(); event.stopPropagation();
    const document = latest.current.project;
    const id = event.dataTransfer.getData(ASSET_MIME);
    const asset = document.assets.find(item => item.id === (id || draggedAsset.current?.id));
    const track = document.tracks.find(item => item.id === trackId);
    const raw = clientXToTime(event.clientX, { ...readGeometry(), scrollLeft: scroll.current.scrollLeft, zoom: zoomRef.current, duration: 1800 });
    const point = snapTime(raw, document, { threshold: pixelsToTime(8, zoomRef.current), playhead: latest.current.time });
    const length = asset?.kind === 'image' ? 5 : asset?.duration;
    const available = Boolean(asset && (trackId ? track && compatibleTrack(track, asset) : document.tracks.length < 16 || document.tracks.some(item => compatibleTrack(item, asset) && !item.clips.length)));
    const valid = available && clipCount(document) < 80 && Number.isFinite(length) && length > 0 && point + length <= 1800;
    event.dataTransfer.dropEffect = valid ? 'copy' : 'none';
    if (commit) {
      setDropFeedback(null); setSnapGuide(null); draggedAsset.current = null;
      if (valid) latest.current.onChange(value => trackId ? insertAssets(value, [asset], point, trackId) : insertAssets(value, [asset], point), 'Agregar medios');
      return;
    }
    feedback(trackId || 'new', valid, valid ? track ? `Agregar a ${track.name}` : 'Agregar en nueva pista' : track?.locked ? 'Pista bloqueada' : asset ? 'Destino incompatible o limite del proyecto' : 'Medio no disponible');
    setSnapGuide(valid && Math.abs(point - raw) > 0.00001 ? point : null);
  };
  const leaveDrop = (event, trackId) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setDropFeedback(previous => previous?.trackId === (trackId || 'new') ? null : previous);
    setSnapGuide(null);
  };
  const dropState = trackId => dropFeedback?.trackId === trackId ? dropFeedback.valid ? 'valid' : 'invalid' : undefined;
  const dropMessage = trackId => dropFeedback?.trackId === trackId && <span className="creator-lane-drop-feedback" role="status" style={{ left: visibleLeft + 8 }}>{dropFeedback.message}</span>;
  const guide = snapGuide !== null && <i className="creator-snap-guide" data-testid="creator-snap-guide" aria-hidden="true" style={{ left: timeToPixels(snapGuide, scaleZoom) }} />;
  return <div className="creator-timeline-scroll creator-timeline-interactive" ref={scroll} data-testid="creator-timeline" aria-label="Timeline multipista" data-interaction={interaction || undefined}
    data-drag-valid={dropFeedback?.valid === false ? 'false' : undefined}
    onPointerDownCapture={startPan} onPointerMove={event => { moveGesture(event); moveDrag(event); }}
    onPointerUp={event => { finishGesture(event); finishDrag(event); }}
    onPointerCancel={event => { finishGesture(event, true); finishDrag(event, true); }}
    onLostPointerCapture={event => { finishGesture(event, true); finishDrag(event, true); }}
    onKeyDown={event => {
      if (event.key === 'Escape' && drag.current) { event.preventDefault(); event.stopPropagation(); finishDrag({ pointerId: drag.current.pointerId }, true); }
    }}
    onAuxClick={event => { if (event.button === 1) event.preventDefault(); }}
    onScroll={() => {
      if (scrollFrame.current !== null) return;
      scrollFrame.current = window.requestAnimationFrame(() => { scrollFrame.current = null; if (scroll.current) setVisibleLeft(scroll.current.scrollLeft); });
    }}>
    <div className="creator-timeline-inner" style={{ width: width + geometry.headerWidth, '--creator-timeline-grid': `${timeToPixels(step, scaleZoom)}px` }}>
      <div className="creator-ruler-row">
        <div className="creator-track-heading" ref={heading}>PISTAS <span>{project.tracks.length}</span></div>
        <div className="creator-ruler" data-testid="creator-ruler" style={{ width }} onPointerDown={event => startScrub(event)} role="slider" aria-label="Posicion del cabezal" aria-valuemin={0}
          aria-valuemax={total} aria-valuenow={time} aria-valuetext={formatTime(time)} tabIndex={0} onKeyDown={event => {
            if (['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
              event.preventDefault(); event.stopPropagation();
              onSeek(event.key === 'Home' ? 0 : event.key === 'End' ? total : clamp(time + (event.key === 'ArrowRight' ? 1 : -1) / (project.canvas.fps || 30), 0, total));
            }
          }}>
          {ticks.map(index => <span key={index} className="creator-tick" style={{ left: timeToPixels(index * step, scaleZoom) }}>{step < 1 ? (index * step).toFixed(1) : formatTime(index * step).slice(0, 5)}</span>)}
          {current.markers.map(marker => <button type="button" key={marker.id} title={marker.label} aria-label={`Ir a ${marker.label}`} className="creator-marker" style={{ left: timeToPixels(marker.time, scaleZoom) }}
            onPointerDown={event => event.stopPropagation()} onClick={() => onSeek(marker.time)} />)}
          <i className="creator-playhead" style={{ left: timeToPixels(time, scaleZoom) }} />
          {guide}
        </div>
      </div>
      {[...current.tracks].reverse().map(track => <div className="creator-track-row" key={track.id} style={{ '--track-height': `${TRACK_HEIGHTS[track.height || 'normal']}px` }}>
        <CreatorTrackHeader {...{ track, project: current, onChange }} />
        <div className="creator-track-lane" data-track-lane={track.id} data-drop-state={dropState(track.id)} data-testid={`creator-lane-${track.id}`} style={{ width }} onPointerDown={event => startScrub(event, true)}
          onDragOver={event => assetDrop(event, track.id)} onDrop={event => assetDrop(event, track.id, true)} onDragLeave={event => leaveDrop(event, track.id)}>
          {track.clips.map(clip => {
            const asset = project.assets.find(item => item.id === clip.asset_id);
            const pixelWidth = timeToPixels(clip.duration, scaleZoom);
            const left = timeToPixels(clip.start, scaleZoom);
            const dragging = drag.current?.ids.includes(clip.id);
            const offscreen = !dragging && !selected.includes(clip.id) && (left + pixelWidth < visibleLeft - 240 || left > visibleLeft + usableWidth + 240);
            return <div key={clip.id} role="button" tabIndex={0} aria-label={`${clip.text ? 'Titulo' : asset?.filename || track.name}, ${formatTime(clip.start)}, ${clip.duration.toFixed(2)} segundos`}
              aria-pressed={selected.includes(clip.id)} data-testid={`creator-clip-${clip.id}`}
              className={`creator-clip creator-clip-${track.type} ${selected.includes(clip.id) ? 'is-selected' : ''} ${track.locked ? 'is-locked' : ''} ${dragging ? 'is-dragging' : ''} ${offscreen ? 'is-offscreen' : ''}`}
              style={{ left, width: Math.max(5, pixelWidth) }}
              onPointerDown={event => startDrag(event, clip, track)} onContextMenu={event => clipContext(event, clip.id)}
              onKeyDown={event => {
                if (event.key === 'ContextMenu' || event.key === 'F10' && event.shiftKey) { clipContext(event, clip.id); return; }
                if (event.key === 'Enter') { event.stopPropagation(); onSelect([clip.id]); }
              }}>
              <span className="creator-clip-label" title={clip.text?.content || asset?.filename || track.name}>{clip.text?.content || asset?.filename || track.name}</span>
              {asset?.peaks?.length > 0 && <CreatorWaveform asset={asset} clip={clip} />}
              {asset?.kind === 'video' && <CreatorVideoThumbnails {...{ asset, clip, sessionUrl, pixelWidth }} />}
              {asset?.kind === 'image' && asset.thumbnail_url && <img src={sessionUrl(asset.thumbnail_url)} alt="" draggable="false" referrerPolicy="no-referrer" className="creator-clip-thumb" />}
              {selected.includes(clip.id) && pixelWidth > 20 && visibleKeyframes(clip).map(frame => <button type="button" key={`${frame.property}-${frame.time}`} className="creator-keyframe-marker" title={`${frame.property} · ${formatTime(keyframeAbsoluteTime(clip, frame.time))}`} aria-label={`Ir a keyframe ${frame.property} ${frame.time}`}
                style={{ left: timeToPixels(keyframeAbsoluteTime(clip, frame.time) - clip.start, scaleZoom) }} onPointerDown={event => event.stopPropagation()} onClick={event => { event.stopPropagation(); onSeek(keyframeAbsoluteTime(clip, frame.time)); }}><Diamond size={9} /></button>)}
              {onTransitionSelect && previousTransitionClip(current, clip.id) && <button type="button" className="creator-transition-handle" title="Editar transicion de entrada" aria-label="Editar transicion de entrada" onPointerDown={event => event.stopPropagation()} onClick={event => { event.stopPropagation(); onTransitionSelect(clip.id); }}><ArrowLeftRight size={12} /></button>}
              {!track.locked && ['left', 'right'].map(edge => <span key={edge} role="button" tabIndex={0} title={edge === 'left' ? 'Recortar inicio' : 'Recortar final'} aria-label={edge === 'left' ? 'Recortar inicio' : 'Recortar final'} className={`creator-trim creator-trim-${edge}`}
                onPointerDown={event => startDrag(event, clip, track, edge)} onKeyDown={event => {
                  if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
                  event.preventDefault(); event.stopPropagation();
                  const target = (edge === 'left' ? clip.start : clip.start + clip.duration) + (event.key === 'ArrowRight' ? 1 : -1) / project.canvas.fps;
                  onChange(value => trimClip(value, clip.id, edge, target), 'Recortar');
                }} />)}
            </div>;
          })}
          <i className="creator-playhead" style={{ left: timeToPixels(time, scaleZoom) }} />
          {guide}{dropMessage(track.id)}
        </div>
      </div>)}
      {!project.tracks.length && <div className="creator-track-row"><div className="creator-track-heading" aria-hidden="true" />
        <div className="creator-track-lane creator-empty-timeline" data-testid="creator-empty-lane" style={{ width }} onPointerDown={event => startScrub(event, true)}
          onDragOver={event => assetDrop(event)} onDrop={event => assetDrop(event, undefined, true)} onDragLeave={event => leaveDrop(event)}>Sin pistas</div></div>}
      <div className="creator-track-row creator-new-track-row"><div className="creator-track-heading"><Plus size={13} aria-hidden="true" />Nueva pista</div>
        <div className="creator-track-lane creator-new-track-drop" data-testid="creator-new-track-drop" data-drop-state={dropState('new')} style={{ width }}
          onPointerDown={event => startScrub(event, true)} onDragOver={event => assetDrop(event)} onDrop={event => assetDrop(event, undefined, true)} onDragLeave={event => leaveDrop(event)}>
          {guide}{dropMessage('new')}
        </div></div>
    </div>
  </div>;
});

export default CreatorTimeline;
